import React, { Component, ErrorInfo, ReactNode } from 'react';
import { chunkErrorHandler } from '@/utils/chunkErrorHandler';
import Button from '@/components/UI/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isChunkError: boolean;
  retryCount: number;
}

/**
 * Enhanced Error Boundary that specifically handles chunk loading errors
 * and provides automatic recovery mechanisms
 */
class ChunkErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if this is a chunk loading error
    const isChunkError = ChunkErrorBoundary.isChunkLoadingError(error);
    
    return {
      hasError: true,
      error,
      isChunkError
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log the error for debugging
    console.error('ChunkErrorBoundary caught an error:', error, errorInfo);

    // If it's a chunk error, try to handle it automatically
    if (this.state.isChunkError && this.state.retryCount < this.maxRetries) {
      this.handleChunkError(error);
    }

    // Track error in analytics
    if ((window as any).gtag) {
      (window as any).gtag('event', 'error_boundary_triggered', {
        error_message: error.message,
        error_stack: error.stack,
        is_chunk_error: this.state.isChunkError,
        retry_count: this.state.retryCount,
        page_url: window.location.href
      });
    }
  }

  private static isChunkLoadingError(error: Error): boolean {
    const chunkErrorPatterns = [
      /Loading chunk \d+ failed/,
      /Failed to fetch dynamically imported module/,
      /Loading CSS chunk \d+ failed/,
      /ChunkLoadError/,
      /NetworkError/,
      /Failed to import/
    ];

    return chunkErrorPatterns.some(pattern => 
      pattern.test(error.message) || pattern.test(error.name)
    );
  }

  private async handleChunkError(error: Error) {
    try {
      await chunkErrorHandler.handleError(error);
    } catch (handlerError) {
      console.error('Failed to handle chunk error:', handlerError);
      // If automatic handling fails, show manual retry option
      this.setState({ retryCount: this.state.retryCount + 1 });
    }
  }

  private handleRetry = () => {
    if (this.state.isChunkError) {
      // For chunk errors, reload the page
      window.location.reload();
    } else {
      // For other errors, reset the error boundary
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        isChunkError: false,
        retryCount: 0
      });
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Chunk error specific UI
      if (this.state.isChunkError) {
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="mb-6">
                <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  App Update Available
                </h2>
                <p className="text-gray-600 mb-4">
                  HEAT Training has been updated with new features. Please refresh to get the latest version.
                </p>
              </div>

              <div className="space-y-3">
                <Button 
                  onClick={this.handleReload}
                  className="w-full"
                >
                  Refresh App
                </Button>
                
                <Button 
                  onClick={this.handleGoHome}
                  variant="outline"
                  className="w-full"
                >
                  Go to Homepage
                </Button>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  If this problem persists, please contact support.
                </p>
              </div>
            </div>
          </div>
        );
      }

      // General error UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Something went wrong
              </h2>
              <p className="text-gray-600 mb-4">
                We encountered an unexpected error. Please try again.
              </p>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={this.handleRetry}
                className="w-full"
              >
                Try Again
              </Button>
              
              <Button 
                onClick={this.handleReload}
                variant="outline"
                className="w-full"
              >
                Refresh Page
              </Button>
              
              <Button 
                onClick={this.handleGoHome}
                variant="outline"
                className="w-full"
              >
                Go to Homepage
              </Button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 pt-4 border-t border-gray-200 text-left">
                <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                  Error Details (Development)
                </summary>
                <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono text-gray-800 overflow-auto max-h-40">
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error.message}
                  </div>
                  {this.state.error.stack && (
                    <div>
                      <strong>Stack:</strong>
                      <pre className="whitespace-pre-wrap">{this.state.error.stack}</pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                Error ID: {Date.now()}-{Math.random().toString(36).substr(2, 9)}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
